import {
  CHARGE_CANCELLATION_NOTES,
  ORDER_EVENT_LABELS,
  ORDER_STATUS_LABELS,
  ORDER_TRANSITIONS
} from './order-status.constant';
import { seatKindLabel, seatSurchargeLabel } from './seat-map/seat-map.helper';

function cents(value) {
  return Number(value || 0) / 100;
}

export function orderNumber(id) {
  return String(id || '')
    .slice(0, 8)
    .toUpperCase();
}

export function lineDescription(item) {
  const snapshot = item.snapshot || {};
  const parts = [
    snapshot.variant && snapshot.variant.name,
    snapshot.tariffCategory && snapshot.tariffCategory.name,
    snapshot.session && snapshot.session.startsAt
      ? new Date(snapshot.session.startsAt).toLocaleString('pt-BR', {
          dateStyle: 'short',
          timeStyle: 'short'
        })
      : null,
    ...(snapshot.options || []).map((option) => option.name)
  ].filter(Boolean);
  return parts.join(' · ');
}

export function toOrderSeat(record) {
  const seat = {
    id: record.id,
    rotulo: record.label,
    tipo: record.kind || 'standard',
    premiumRotulo: record.premium_label || '',
    acrescimoCentavos: record.surcharge_cents || 0
  };
  const notes = [
    seat.tipo !== 'standard' ? seatKindLabel(seat) : '',
    seatSurchargeLabel(seat)
  ].filter(Boolean);
  return {
    ...seat,
    acrescimo: cents(seat.acrescimoCentavos),
    descricao: notes.length ? `${seat.rotulo} (${notes.join(' ')})` : seat.rotulo
  };
}

function itemSeats(item) {
  const seats = item.seats || (item.snapshot && item.snapshot.seats) || [];
  return seats.map(toOrderSeat);
}

export function toOrderDetail(record) {
  return {
    id: record.id,
    numero: orderNumber(record.id),
    contatoId: record.contact_id,
    conversaId: record.conversation_id || null,
    cliente:
      (record.contact && (record.contact.name || record.contact.company_name)) || '(sem nome)',
    status: record.status,
    pagamento: record.payment_status,
    subtotal: cents(record.subtotal_cents),
    desconto: cents(record.discount_cents),
    taxas: cents(record.fee_cents),
    entrega: cents(record.delivery_cents),
    total: cents(record.total_cents),
    formaEntrega: record.fulfillment_mode,
    dadosEntrega: record.fulfillment || {},
    observacoes: record.notes,
    reservaAte: record.hold_expires_at,
    criadoEm: record.created_at,
    itens: (record.items || []).map((item) => ({
      id: item.id,
      nome: (item.snapshot && item.snapshot.product && item.snapshot.product.name) || 'Item',
      detalhes: lineDescription(item),
      quantidade: item.quantity,
      assentos: itemSeats(item),
      unitario: cents(item.unit_price_cents),
      total: cents(item.total_cents || item.unit_price_cents * item.quantity)
    })),
    pagamentos: (record.payments || []).map((payment) => ({
      id: payment.id,
      metodo: payment.method,
      status: payment.status,
      valor: cents(payment.amount_cents),
      criadoEm: payment.created_at
    })),
    confirmacaoEnviada: (record.events || []).some((event) => event.type === 'confirmation_sent'),
    eventos: (record.events || []).map((event) => ({
      id: event.id,
      descricao: eventDescription(event),
      criadoEm: event.created_at
    }))
  };
}

export function eventDescription(event) {
  const label = ORDER_EVENT_LABELS[event.type] || event.type;
  if (event.type === 'status_changed' && event.to_status) {
    return `${label}: ${ORDER_STATUS_LABELS[event.to_status] || event.to_status}`;
  }
  const payload = event.payload || {};
  const notes = [voucherCountNote(event), payload.reason, ...cancellationNotes(event)].filter(
    Boolean
  );
  return notes.length ? `${label}: ${notes.join(' · ')}` : label;
}

function voucherCountNote(event) {
  const count = event.payload && event.payload.count;
  if (!['vouchers_issued', 'vouchers_cancelled'].includes(event.type) || !count) return '';
  return `${count} documento(s)`;
}

function cancellationNotes(event) {
  if (event.type !== 'cancelled' || !event.payload) return [];
  const charges = (event.payload.charges || [])
    .map((charge) => CHARGE_CANCELLATION_NOTES[charge.outcome])
    .filter(Boolean);
  return event.payload.refund_required
    ? [...charges, 'pedido já pago: o reembolso deve ser feito no provedor de pagamento']
    : charges;
}

export function nextStatuses(status) {
  return (ORDER_TRANSITIONS[status] || []).filter((next) => next !== 'cancelled');
}

export function canCancel(order) {
  return (ORDER_TRANSITIONS[order.status] || []).includes('cancelled');
}

export function emptyCartLine() {
  return {
    produtoId: '',
    varianteId: '',
    ofertaId: '',
    sessaoId: '',
    tarifaId: '',
    opcoes: {},
    quantidade: 1,
    detalhe: null,
    lugares: null,
    assentosIds: [],
    carregandoAssentos: false,
    assentosErro: ''
  };
}

export function selectedOptionIds(line) {
  return Object.entries(line.opcoes || {})
    .filter(([, selected]) => selected)
    .map(([id]) => id);
}

export function isSeatedLine(line) {
  return Boolean(line && line.lugares && line.lugares.assentos.length);
}

export function missingSession(line) {
  return Boolean(line && line.detalhe && line.detalhe.reserva === 'session' && !line.sessaoId);
}

export function selectedSeats(line) {
  if (!isSeatedLine(line)) return [];
  return (line.assentosIds || [])
    .map((id) => line.lugares.assentos.find((seat) => seat.id === id))
    .filter(Boolean);
}

export function cartItems(lines) {
  return lines
    .filter((line) => line.produtoId)
    .map((line) => {
      const seated = isSeatedLine(line);
      const item = {
        productId: line.produtoId,
        quantity: seated ? line.assentosIds.length : Number(line.quantidade) || 1
      };
      if (seated) item.seatIds = [...line.assentosIds];
      if (line.varianteId) item.variantId = line.varianteId;
      if (line.ofertaId) item.offerId = line.ofertaId;
      if (line.sessaoId) item.sessionId = line.sessaoId;
      if (line.tarifaId) item.tariffCategoryId = line.tarifaId;
      const optionIds = selectedOptionIds(line);
      if (optionIds.length) item.optionIds = optionIds;
      return item;
    });
}

export function cartRequest(cart) {
  const request = { items: cartItems(cart.linhas) };
  if (cart.canal) request.salesChannel = cart.canal;
  if (cart.formaEntrega) request.fulfillmentMode = cart.formaEntrega;
  return request;
}

export function orderRequest(cart, idempotencyKey, conversationId) {
  const request = { ...cartRequest(cart), contactId: cart.contatoId, idempotencyKey };
  if (conversationId) request.conversationId = conversationId;
  if (cart.formaEntrega === 'delivery' && cart.endereco)
    request.fulfillment = { address: cart.endereco.trim() };
  if (cart.observacoes && cart.observacoes.trim()) request.notes = cart.observacoes.trim();
  return request;
}

export function chargeRequest(orderId, payment) {
  const payer = { document: (payment.documento || '').replace(/\D/g, '') };
  if ((payment.email || '').trim()) payer.email = payment.email.trim();
  return { method: payment.metodo, payer, idempotencyKey: `painel:${orderId}:${payment.metodo}` };
}

export function pendingChargeId(order) {
  const pending = (order.pagamentos || []).filter((payment) => payment.status === 'pending');
  return pending.length ? pending[pending.length - 1].id : null;
}

export function cartErrors(cart) {
  const errors = {};
  if (!cart.contatoId) errors.contato = 'Escolha o cliente.';
  if (!cartItems(cart.linhas).length) errors.itens = 'Adicione ao menos um item.';
  if (cart.linhas.some((line) => line.produtoId && !(Number(line.quantidade) >= 1))) {
    errors.itens = 'Informe quantidades a partir de 1.';
  }
  const withoutSession = cart.linhas.find(missingSession);
  if (withoutSession)
    errors.itens = `Escolha a data ou sessão de "${withoutSession.detalhe.nome}".`;
  const unseated = cart.linhas.find((line) => isSeatedLine(line) && !line.assentosIds.length);
  if (unseated)
    errors.itens = `Escolha os assentos de "${unseated.detalhe ? unseated.detalhe.nome : 'item'}".`;
  if (cart.formaEntrega === 'delivery' && !(cart.endereco || '').trim())
    errors.endereco = 'Informe o endereço de entrega.';
  return errors;
}
